import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";

/** Demo data: one extreme point (peak), rest moderate. */
const DEMO_MAY_DATA = [
  { date: "2025-05-02", intensity: 5.5, emotion: "calm", timestamp: "2025-05-02T10:00:00Z" },
  { date: "2025-05-06", intensity: 5, emotion: "anxious", timestamp: "2025-05-06T14:00:00Z" },
  { date: "2025-05-10", intensity: 10, emotion: "happy", timestamp: "2025-05-10T09:00:00Z" },
  { date: "2025-05-14", intensity: 5, emotion: "neutral", timestamp: "2025-05-14T18:00:00Z" },
  { date: "2025-05-18", intensity: 6.5, emotion: "confident", timestamp: "2025-05-18T11:00:00Z" },
  { date: "2025-05-22", intensity: 5.5, emotion: "calm", timestamp: "2025-05-22T16:00:00Z" },
  { date: "2025-05-26", intensity: 6, emotion: "happy", timestamp: "2025-05-26T12:00:00Z" },
];

const EMOTION_COLORS: Record<string, string> = {
  calm: "#38bdf8",
  excited: "#a78bfa",
  happy: "#4ade80",
  confident: "#f59e0b",
  neutral: "#94a3b8",
  anxious: "#facc15",
  sad: "#fb7185",
  stressed: "#fb7185",
};

const getColor = (emotion: string) =>
  EMOTION_COLORS[emotion?.toLowerCase()] ?? "#94a3b8";

const width = 1100;
const height = 360;
const pad = { top: 30, right: 25, bottom: 45, left: 50 };
const gw = width - pad.left - pad.right;
const gh = height - pad.top - pad.bottom;

// Slower: ~5.5s for line, then extra for final dot
const SEGMENT_DURATION_BASE = 5.5;
const EXTRA_DOT_TIME = 0.8;

export function EmoGraphAnimation() {
  const [isPlaying, setIsPlaying] = useState(true);
  const [runKey, setRunKey] = useState(0);
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  const points = DEMO_MAY_DATA;
  const uniqueEmotions = Array.from(new Set(points.map((p) => p.emotion.toLowerCase()))).sort();
  const times = points.map((p) => new Date(p.timestamp).getTime());
  const tMin = Math.min(...times);
  const tMax = Math.max(...times);

  const getX = (ts: string) => {
    const t = new Date(ts).getTime();
    if (tMin === tMax) return pad.left + gw / 2;
    return pad.left + ((t - tMin) / (tMax - tMin)) * gw;
  };
  const getY = (intensity: number) =>
    pad.top + gh - (intensity / 10) * gh;

  const nSegments = Math.max(points.length - 1, 1);
  const segmentDuration = SEGMENT_DURATION_BASE / nSegments;
  const totalAnimationSec = nSegments * segmentDuration + EXTRA_DOT_TIME;

  const curvePath =
    points.length === 1
      ? `M ${getX(points[0].timestamp)} ${getY(points[0].intensity)}`
      : points.reduce((acc, p, i, arr) => {
          const x = getX(p.timestamp);
          const y = getY(p.intensity);
          if (i === 0) return `M ${x} ${y}`;
          const px = getX(arr[i - 1].timestamp);
          const py = getY(arr[i - 1].intensity);
          const cx = (px + x) / 2;
          return acc + ` C ${cx} ${py}, ${cx} ${y}, ${x} ${y}`;
        }, "");

  const bottomY = pad.top + gh;
  const areaPath =
    points.length >= 2
      ? `${curvePath} L ${getX(points[points.length - 1].timestamp)} ${bottomY} L ${getX(points[0].timestamp)} ${bottomY} Z`
      : "";

  // Loop: when one run finishes, start again if still playing
  useEffect(() => {
    if (!isPlaying) return;
    const t = setTimeout(() => {
      if (isPlayingRef.current) setRunKey((k) => k + 1);
    }, totalAnimationSec * 1000);
    return () => clearTimeout(t);
  }, [isPlaying, runKey, totalAnimationSec]);

  return (
    <div className="relative w-full max-w-6xl mx-auto">
      <div className="flex items-center justify-center gap-3 mb-3">
        <p className="text-center text-slate-400 text-sm sm:text-base">EmoGraph — May</p>
        <button
          type="button"
          onClick={() => setIsPlaying(false)}
          className="text-xs sm:text-sm px-2 py-1 rounded bg-white/10 text-slate-300 hover:bg-white/20 transition"
        >
          Stop animation
        </button>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        style={{ minHeight: '280px' }}
      >
        <defs>
          {/* Segment gradients for the line */}
          {points.slice(0, -1).map((p, i) => {
            const x1 = getX(p.timestamp);
            const y1 = getY(p.intensity);
            const x2 = getX(points[i + 1].timestamp);
            const y2 = getY(points[i + 1].intensity);
            const c1 = getColor(p.emotion);
            const c2 = getColor(points[i + 1].emotion);
            return (
              <linearGradient
                key={`seg-${i}`}
                id={`landing-seg-${i}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={c1} />
                <stop offset="100%" stopColor={c2} />
              </linearGradient>
            );
          })}
          {/* Horizontal blend for area fill (emotion colors left to right) */}
          <linearGradient
            id="landing-areaFillH"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            {points.map((p, i) => (
              <stop
                key={i}
                offset={`${(i / Math.max(points.length - 1, 1)) * 100}%`}
                stopColor={getColor(p.emotion)}
                stopOpacity="0.28"
              />
            ))}
          </linearGradient>
          {/* Vertical fade for area (opacity mask) */}
          <linearGradient id="landing-areaFillV" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity="0.5" />
            <stop offset="100%" stopColor="white" stopOpacity="0.03" />
          </linearGradient>
          <mask id="landing-areaMask">
            <rect
              x="0"
              y="0"
              width={width}
              height={height}
              fill="url(#landing-areaFillV)"
            />
          </mask>
        </defs>

        {/* Y grid */}
        {[0, 2, 4, 6, 8, 10].map((v) => (
          <line
            key={v}
            x1={pad.left}
            y1={getY(v)}
            x2={width - pad.right}
            y2={getY(v)}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={1}
          />
        ))}

        {/* Y labels */}
        {[0, 2, 4, 6, 8, 10].map((v) => (
          <text
            key={`yl-${v}`}
            x={pad.left - 10}
            y={getY(v) + 5}
            textAnchor="end"
            fontSize="11"
            fill="rgba(255,255,255,0.3)"
          >
            {v}
          </text>
        ))}

        {/* Shading below the line — area fill fades in with the line */}
        {areaPath && (
          <motion.path
            key={`area-${runKey}`}
            d={areaPath}
            fill="url(#landing-areaFillH)"
            mask="url(#landing-areaMask)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{
              duration: totalAnimationSec * 0.85,
              ease: "easeInOut",
            }}
          />
        )}

        {/* Glow under line */}
        <motion.path
          key={`glow-${runKey}`}
          d={curvePath}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={10}
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{
            duration: totalAnimationSec * 0.9,
            ease: "easeInOut",
          }}
        />

        {/* Line segments — draw one dot to the next */}
        {points.map((p, i) => {
          if (i >= points.length - 1) return null;
          const next = points[i + 1];
          const x1 = getX(p.timestamp);
          const y1 = getY(p.intensity);
          const x2 = getX(next.timestamp);
          const y2 = getY(next.intensity);
          const cx = (x1 + x2) / 2;
          const segPath = `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
          return (
            <motion.path
              key={`line-${runKey}-${i}`}
              d={segPath}
              fill="none"
              stroke={`url(#landing-seg-${i})`}
              strokeWidth={4}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{
                duration: segmentDuration,
                delay: i * segmentDuration,
                ease: "easeInOut",
              }}
            />
          );
        })}

        {/* Dots — appear as the line reaches each point */}
        {points.map((p, idx) => {
          const x = getX(p.timestamp);
          const y = getY(p.intensity);
          const color = getColor(p.emotion);
          const delay = idx * segmentDuration;
          return (
            <motion.g
              key={`dot-${runKey}-${idx}`}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                delay,
                duration: 0.5,
                ease: [0.22, 0.61, 0.36, 1],
              }}
            >
              <circle cx={x} cy={y} r={10} fill={color} opacity={0.2} />
              <circle
                cx={x}
                cy={y}
                r={6}
                fill={color}
                stroke="rgba(255,255,255,0.8)"
                strokeWidth={2}
              />
            </motion.g>
          );
        })}

        {/* X date labels */}
        {points.map((p) => (
          <text
            key={p.date}
            x={getX(p.timestamp)}
            y={height - pad.bottom + 20}
            textAnchor="middle"
            fontSize="11"
            fill="rgba(255,255,255,0.45)"
            fontWeight="500"
          >
            {format(new Date(p.timestamp), "dd MMM")}
          </text>
        ))}
      </svg>

      {/* Legend: emotion → color */}
      <div className="mt-4 px-1 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs sm:text-sm">
        {uniqueEmotions.map((emotion) => {
          const color = getColor(emotion);
          return (
            <div key={emotion} className="flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-3 rounded-full shrink-0"
                style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}` }}
              />
              <span className="text-slate-400 capitalize">{emotion}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
